import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { Icon } from "@/components/Icon";
import { PopupCloseButton } from "@/components/PopupClose";
import { Spinner } from "@/components/Spinner";
import { useExchangeQuote } from "@/api/hooks";
import {
  popupActionsCol,
  popupBtnPrimary,
  popupBtnSecondary,
} from "@/lib/popupButtons";

/**
 * WithdrawOfferModal (owner 2026-07-27) — перехоплення виводу на картку.
 *
 * Owner: «якщо людина йде виводити на картку — показуємо попап, що ці гроші
 * можна обміняти на підписку, і це навіть дешевше, ніж просто її купити.
 * Точно хочеш вивести — чи міняємо на підписку?»
 *
 * Тому тут не «підтвердження виводу», а порівняння вигоди: скільки днів дасть
 * обмін і скільки ті самі дні коштували б грошима. Вивід лишається доступним
 * другою кнопкою — нікого не замикаємо.
 */
const REGULAR_DAY_UAH = 33; // звичайна ціна (990/30) — для порівняння

function dayWord(n: number): string {
  return n === 1 ? "день" : n % 10 >= 2 && n % 10 <= 4 && (n < 10 || n > 20) ? "дні" : "днів";
}

export function WithdrawOfferModal({
  balance,
  onExchange,
  onWithdraw,
  onClose,
}: {
  balance: number;
  onExchange: () => void;
  /** Далі — крок із номером картки (WithdrawCardModal, owner 2026-10-19). */
  onWithdraw: () => void;
  onClose: () => void;
}) {
  const quote = useExchangeQuote(true);
  const q = quote.data;
  const price = q?.day_price_uah ?? 25;
  const days = q?.days ?? 0;
  // Скільки коштували б ті самі дні за звичайною ціною — це і є вигода.
  const regularCost = days * REGULAR_DAY_UAH;
  const benefit = Math.max(0, regularCost - (q?.cost_uah ?? 0));

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />

        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="💸" size={48} />
          </div>
          <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight mb-1.5">
            Точно вивести на картку?
          </div>
          <div className="text-[0.8125rem] text-ink-dim leading-relaxed [text-wrap:balance]">
            Ці гроші можна обміняти на підписку —{" "}
            <b className="text-ink">дешевше</b>, ніж купувати її за гроші.
          </div>
        </div>

        {quote.isLoading ? (
          <div className="py-8 flex justify-center">
            <Spinner />
          </div>
        ) : days >= 1 && q ? (
          <>
            <div className="rounded-2xl bg-primary/[0.06] ring-1 ring-primary/20 p-4 mb-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-center flex-1">
                  <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
                    Твій баланс
                  </div>
                  <div className="font-display text-[1.375rem] font-bold text-ink leading-none">
                    {q.balance_uah} ₴
                  </div>
                </div>
                <span className="text-primary shrink-0">
                  <Icon name="arrow-right" size={22} />
                </span>
                <div className="text-center flex-1">
                  <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
                    Це підписка на
                  </div>
                  <div className="font-display text-[1.375rem] font-bold text-primary leading-none">
                    {q.days} {dayWord(q.days)}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-bg/50 px-3.5 py-2.5 text-[0.78125rem] mb-3 leading-relaxed">
              <div className="flex items-center justify-between py-1.5 border-b border-ink/[0.05]">
                <span className="text-ink-dim">Обмін</span>
                <span className="font-bold text-primary">{price} ₴ = 1 день</span>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-ink/[0.05]">
                <span className="text-ink-dim">Купувати підписку</span>
                <span className="font-semibold text-ink">
                  ~{REGULAR_DAY_UAH} ₴ / день
                </span>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-ink-dim">
                  Ті самі {q.days} {dayWord(q.days)} коштували б
                </span>
                <span className="font-semibold text-ink">{regularCost} ₴</span>
              </div>
            </div>

            {benefit > 0 ? (
              <p className="text-[0.75rem] text-ink-dim text-center leading-relaxed px-2 mb-1 [text-wrap:balance]">
                Обмін вигідніший приблизно на{" "}
                <b className="text-ink">{benefit} ₴</b>.
              </p>
            ) : null}
          </>
        ) : (
          <div className="rounded-2xl bg-bg/50 p-4 text-center mb-2 text-[0.78125rem] text-ink-dim leading-relaxed [text-wrap:balance]">
            На обмін потрібно мінімум <b className="text-ink">{price} ₴</b> (це
            1 день підписки). Зараз на балансі {q?.balance_uah ?? balance} ₴.
          </div>
        )}

        <div className={popupActionsCol + " mt-4"}>
          {days >= 1 ? (
            <button type="button" onClick={onExchange} className={popupBtnPrimary}>
              Обміняти на підписку
            </button>
          ) : null}
          <button
            type="button"
            onClick={onWithdraw}
            className={days >= 1 ? popupBtnSecondary : popupBtnPrimary}
          >
            {days >= 1 ? "Все одно вивести на картку" : "Вивести на картку"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
